import type {Image} from '@/api/bffSupport';
import type {Props as OverlayProps} from '@/components/PictureGalleryCarouselOverlay/components/Overlay';

import {useBreakpointUp} from '@travel-frontend/core';
import {useCallback, useEffect} from 'react';

import {ImageCarousel} from '@/components/ImageCarousel';
import {ImageGallerySlider} from '@/components/ImageGallerySlider';
import {CarouselContent} from '@/components/PictureGalleryCarouselOverlay/components/CarouselContent';
import {Overlay} from '@/components/PictureGalleryCarouselOverlay/components/Overlay';
import {useSwipeableImages} from '@/hooks/useSwipeableImages';
import {useTracking} from '@/modules/common/hooks/useTracking';

type Props = OverlayProps & {
  images: Array<Image>;
  selectedImageIndex?: number;
};

export const PictureGalleryCarouselOverlay: React.FunctionComponent<Props> = ({
  visible,
  images,
  selectedImageIndex,
  ...restProps
}) => {
  const lgUp = useBreakpointUp('lg');
  const {dispatchGenericTrackingEvent} = useTracking();

  const {
    goBack: handleCarouselGoBack,
    getInternalProps: carouselGetInternalProps,
    goForward: handleCarouselGoForward,
    goToImage: handleCarouselGoToImage,
    activeSlideIndex: carouselActiveSlideIndex,
  } = useSwipeableImages({
    initialImageIndex: selectedImageIndex,
    imageCount: images.length,
    imagesPerSlide: 1,
  });
  const {
    goForward: handleSliderGoForward,
    goBack: handleSliderGoBack,
    getInternalProps: sliderGetInternalProps,
    goToImage: sliderGoToImage,
  } = useSwipeableImages({
    initialImageIndex: selectedImageIndex,
    imageCount: images.length,
    imagesPerSlide: 4,
  });
  const largeImages = images.map((image) => image.large.url);
  const smallImages = images.map((image) => image.small.url);

  const handleEventSlide = useCallback(
    (direction: 'previous' | 'next', component: 'slider' | 'carousel') => {
      const navigationActions = {
        slider: {
          previous: handleSliderGoBack,
          next: handleSliderGoForward,
        },
        carousel: {
          previous: handleCarouselGoBack,
          next: handleCarouselGoForward,
        },
      };

      navigationActions[component][direction]();

      dispatchGenericTrackingEvent({
        event: 'gallery',
        event_action: `gallery_slider_${direction}`,
        non_interaction: false,
      });
    },
    [
      handleSliderGoBack,
      handleSliderGoForward,
      handleCarouselGoBack,
      handleCarouselGoForward,
      dispatchGenericTrackingEvent,
    ]
  );

  useEffect(() => {
    if (visible && selectedImageIndex !== undefined) {
      handleCarouselGoToImage(selectedImageIndex);
    }
  }, [handleCarouselGoToImage, selectedImageIndex, visible]);

  useEffect(() => {
    if (carouselActiveSlideIndex >= 0) {
      sliderGoToImage(carouselActiveSlideIndex);
    }
  }, [carouselActiveSlideIndex, sliderGoToImage]);

  return (
    <Overlay
      {...restProps}
      visible={visible}
      // @ts-expect-error ...
      style={{height: '100%', maxHeight: lgUp ? '730px' : '580px'}}
    >
      <CarouselContent>
        <ImageCarousel
          {...carouselGetInternalProps()}
          images={largeImages}
          onClickPrev={() => handleEventSlide('previous', 'carousel')}
          onClickNext={() => handleEventSlide('next', 'carousel')}
          isRounded
        />
        <ImageGallerySlider
          {...sliderGetInternalProps()}
          images={smallImages}
          activeImage={carouselActiveSlideIndex}
          onClickPrev={() => handleEventSlide('previous', 'slider')}
          onClickNext={() => handleEventSlide('next', 'slider')}
          onClickImage={handleCarouselGoToImage}
        />
      </CarouselContent>
    </Overlay>
  );
};
